import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { isApiError, listPipelines, reorderStages } from "../../lib/crm-api";
import type { Pipeline, Stage } from "../../lib/crm-types";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const STAGE_TYPE_KEYS: Record<string, TranslationKey> = {
  open: "crm.stage.type.open",
  won: "crm.stage.type.won",
  lost: "crm.stage.type.lost",
};

function sortStages(stages: Stage[]): Stage[] {
  return [...stages].sort((a, b) => a.sort_order - b.sort_order);
}

function PipelinesInner(): ReactNode {
  const { t } = useConfig();
  const { hasPermission } = useSession();
  const canManage = hasPermission("crm.pipeline.manage");

  const [pipelines, setPipelines] = useState<Pipeline[] | null>(null);
  const [pipelineId, setPipelineId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listPipelines(true);
        if (cancelled) return;
        setPipelines(page.items);
        const preferred = page.items.find((row) => row.is_default) ?? page.items[0] ?? null;
        if (preferred !== null) {
          setPipelineId(preferred.id);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const pipeline = useMemo(
    () => pipelines?.find((row) => row.id === pipelineId) ?? null,
    [pipelines, pipelineId],
  );
  const stages = useMemo(() => sortStages(pipeline?.stages ?? []), [pipeline]);

  const move = useCallback(
    async (index: number, direction: -1 | 1): Promise<void> => {
      const target = index + direction;
      if (target < 0 || target >= stages.length) {
        return;
      }
      const reordered = [...stages];
      const moving = reordered[index];
      const swap = reordered[target];
      if (moving === undefined || swap === undefined) {
        return;
      }
      reordered[index] = swap;
      reordered[target] = moving;
      setBusy(true);
      setError(null);
      const previous = stages;
      setPipelines((current) =>
        current === null
          ? current
          : current.map((row) =>
              row.id === pipelineId ? { ...row, stages: reordered } : row,
            ),
      );
      try {
        const result = await reorderStages(pipelineId, reordered.map((stage) => stage.id));
        setPipelines((current) =>
          current === null
            ? current
            : current.map((row) =>
                row.id === pipelineId ? { ...row, stages: result.items } : row,
              ),
        );
      } catch (caught) {
        setPipelines((current) =>
          current === null
            ? current
            : current.map((row) => (row.id === pipelineId ? { ...row, stages: previous } : row)),
        );
        setError(isApiError(caught) ? caught.detail : t("error.network"));
      } finally {
        setBusy(false);
      }
    },
    [pipelineId, stages, t],
  );

  return (
    <section className="page" aria-labelledby="crm-pipelines-title">
      <header className="page__header">
        <h1 id="crm-pipelines-title">{t("crm.pipelines.title")}</h1>
        <p className="muted">{t("crm.pipelines.subtitle")}</p>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      {pipelines !== null && pipelines.length > 1 ? (
        <label className="field filters__field-inline" htmlFor="pipelines-select">
          <span>{t("crm.deal.pipeline")}</span>
          <select
            id="pipelines-select"
            value={pipelineId}
            onChange={(event) => setPipelineId(event.target.value)}
          >
            {pipelines.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && pipeline !== null && stages.length === 0 ? (
        <p className="empty">{t("crm.pipelines.empty")}</p>
      ) : null}

      {!loading && pipeline !== null && stages.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("crm.pipelines.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("crm.pipeline.order")}</th>
                <th scope="col">{t("crm.pipeline.stageName")}</th>
                <th scope="col">{t("crm.pipeline.stageType")}</th>
                <th scope="col">{t("crm.pipeline.probability")}</th>
                {canManage ? (
                  <th scope="col" className="table__actions-col">
                    <span className="sr-only">{t("crm.common.actions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {stages.map((stage, index) => (
                <tr key={stage.id}>
                  <td>{index + 1}</td>
                  <td>{stage.name}</td>
                  <td>
                    {STAGE_TYPE_KEYS[stage.type] === undefined
                      ? stage.type
                      : t(STAGE_TYPE_KEYS[stage.type] as TranslationKey)}
                  </td>
                  <td>{stage.probability}%</td>
                  {canManage ? (
                    <td className="table__actions-col">
                      <span className="inline-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busy || index === 0}
                          onClick={() => void move(index, -1)}
                        >
                          {t("crm.pipeline.moveUp")}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busy || index === stages.length - 1}
                          onClick={() => void move(index, 1)}
                        >
                          {t("crm.pipeline.moveDown")}
                        </button>
                      </span>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {canManage ? <p className="muted">{t("crm.pipelines.reorderHint")}</p> : null}
    </section>
  );
}

export function PipelinesPage(): ReactNode {
  return (
    <RequirePermission permission="crm.pipeline.read">
      <PipelinesInner />
    </RequirePermission>
  );
}
